import { useEffect, useRef } from "react";
import { checkForNewVersion, reloadForVersion } from "../lib/appVersion";

/**
 * Тихо обновяване на екраните БЕЗ отворена работа (ПИН клавиатурата на общия
 * терминал и /login): при показване и при всяко връщане на фокус пита сървъра
 * за нова версия и презарежда документа, ако има такава.
 *
 * ЕДИНСТВЕНОТО място с автоматично презареждане в приложението (docs/pwa.md).
 * Без него новият билд не влиза НИКОГА в инсталирано PWA: прозорецът на
 * касата стои отворен с дни (иконата само фокусира, няма адресна лента и F5),
 * а изходът и входът са SPA `navigate()`. Смяната на смяна минава през ПИН
 * екрана — там презареждането е безплатно.
 *
 * `canReload` е за момента, в който човекът вече пише (ПИН цифри, отворен
 * модал): проверява се и ПРЕДИ, и СЛЕД заявката (заедно с това дали екранът
 * още е монтиран), за да не изтрием написаното заради отговор, дошъл
 * междувременно. Пропуснатата проверка не се губи —
 * следващият изход към ПИН я прави наново.
 */
export function useReloadOnNewVersion(canReload: boolean = true): void {
  const canReloadRef = useRef(canReload);
  canReloadRef.current = canReload;

  useEffect(() => {
    // `checkForNewVersion` може да чака новия service worker до минута; ако
    // междувременно човекът е влязъл и екранът е демонтиран, `canReloadRef`
    // пази последната си стойност — без този флаг презареждането би паднало
    // върху вече започнатата работа.
    let isMounted = true;
    const checkAndReload = async () => {
      if (!canReloadRef.current) return;
      const serverBuildId = await checkForNewVersion();
      if (!serverBuildId || !isMounted || !canReloadRef.current) return;
      reloadForVersion(serverBuildId);
    };

    void checkAndReload();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkAndReload();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
}
